import { useCallback, useEffect, useMemo, useState } from 'react';
import { PaymentsProp } from '@wetags/payments';
import { useTranslation } from '@frontend/i18n';
import {
  StripeRegisterTerminalComponents,
  RegisterTerminalModalStripeSteps,
  STRIPE_REGISTER_TERMINAL_STEPS_TITLES,
  RegisterTerminalModalJustifiSteps,
  STRIPE_S710_REGISTER_TERMINAL_STEPS_TITLES,
} from '@frontend/payments-register-terminal';
import { useMultiStepControl, ContentLoader } from '@frontend/design-system';
import { useCollectPaymentMultiContext } from '../../../collect-payment-multi.context';
import { RegisterTerminalNavigationSwitch } from './register-terminal-navigation-switch';
import { RegisterTerminalStepBodyProps } from './register-terminal.types';
import { RegisterTerminalStepper } from './stepper/register-terminal-stepper';

const STEPPER_MIN_WIDTH = 776;
const SUCCESS_SCREEN_MIN_WIDTH = 480;

export const RegisterTerminalStripe = () => {
  return <RegisterTerminalNavigationSwitch body={RegisterTerminalStripeBody} />;
};

const RegisterTerminalStripeBody = ({
  onGoBack,
  onCancel,
  onComplete,
  onRegisterTerminal,
}: RegisterTerminalStepBodyProps) => {
  const { t } = useTranslation('payments');
  const { setMinWidth, resetMinWidth } = useCollectPaymentMultiContext();
  const [hideStepper, setHideStepper] = useState(false);

  const {
    stripeData: { stripeLocationId },
    activeLocationId,
  } = useCollectPaymentMultiContext();

  const [S710TerminalDesignsWetag, isLoading] = PaymentsProp.useWetag('s710-register-terminal', activeLocationId);

  const multiStepControl = useMultiStepControl({
    onComplete: () => {
      onComplete();
    },
    onCancel: () => {
      onCancel();
    },
  });

  const childGoBack = useCallback(() => {
    multiStepControl.goPrevStep();
  }, [multiStepControl.goPrevStep]);

  const goToChildStep = useCallback(
    (step: RegisterTerminalModalJustifiSteps | RegisterTerminalModalStripeSteps) => {
      multiStepControl.goToStep({ orStep: step });
    },
    [multiStepControl.goToStep]
  );

  useEffect(() => {
    setMinWidth(hideStepper ? SUCCESS_SCREEN_MIN_WIDTH : STEPPER_MIN_WIDTH);
  }, [hideStepper, setMinWidth]);

  useEffect(() => {
    return () => {
      resetMinWidth();
    };
  }, []);

  const steps = useMemo(() => {
    if (S710TerminalDesignsWetag) {
      return [
        {
          id: RegisterTerminalModalStripeSteps.SetupTerminal,
          title: STRIPE_S710_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.SetupTerminal].heading,
          stepperLabel:
            STRIPE_S710_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.SetupTerminal].title,
          component: <StripeRegisterTerminalComponents.SetupTerminal goBack={onGoBack} goToStep={goToChildStep} />,
        },
        {
          id: RegisterTerminalModalStripeSteps.ConnectWifi,
          title: STRIPE_S710_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.ConnectWifi].heading,
          stepperLabel: STRIPE_S710_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.ConnectWifi].title,
          component: <StripeRegisterTerminalComponents.ConnectWifi goBack={childGoBack} goToStep={goToChildStep} />,
        },
        {
          id: RegisterTerminalModalStripeSteps.RegisterTerminal,
          title: STRIPE_S710_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.RegisterTerminal].heading,
          stepperLabel:
            STRIPE_S710_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.RegisterTerminal].title,
          component: (
            <StripeRegisterTerminalComponents.RegisterTerminal
              goBack={childGoBack}
              goToStep={goToChildStep}
              onSuccess={onComplete}
              onTerminalPaired={onRegisterTerminal}
              locationId={activeLocationId}
              stripeLocationId={stripeLocationId}
              onStatusScreenChange={setHideStepper}
            />
          ),
        },
      ];
    }
    return [
      {
        id: RegisterTerminalModalStripeSteps.InstallBattery,
        title: STRIPE_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.InstallBattery].heading,
        stepperLabel: STRIPE_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.InstallBattery].title,
        component: <StripeRegisterTerminalComponents.InstallBattery goBack={onGoBack} goToStep={goToChildStep} />,
      },
      {
        id: RegisterTerminalModalStripeSteps.PlugInTerminal,
        parentId: RegisterTerminalModalStripeSteps.InstallBattery,
        title: STRIPE_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.PlugInTerminal].heading,
        stepperLabel: STRIPE_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.PlugInTerminal].title,
        component: <StripeRegisterTerminalComponents.PlugInTerminal goBack={childGoBack} goToStep={goToChildStep} />,
      },
      {
        id: RegisterTerminalModalStripeSteps.TurnTerminalOn,
        parentId: RegisterTerminalModalStripeSteps.InstallBattery,
        title: STRIPE_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.TurnTerminalOn].heading,
        stepperLabel: STRIPE_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.TurnTerminalOn].title,
        component: <StripeRegisterTerminalComponents.TurnTerminalOn goBack={childGoBack} goToStep={goToChildStep} />,
      },
      {
        id: RegisterTerminalModalStripeSteps.ConnectToNetwork,
        title: STRIPE_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.ConnectToNetwork].heading,
        stepperLabel: STRIPE_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.ConnectToNetwork].title,
        component: <StripeRegisterTerminalComponents.ConnectToNetwork goBack={childGoBack} goToStep={goToChildStep} />,
      },
      {
        id: RegisterTerminalModalStripeSteps.GeneratePairingCode,
        title: STRIPE_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.GeneratePairingCode].heading,
        stepperLabel: STRIPE_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.GeneratePairingCode].title,
        component: (
          <StripeRegisterTerminalComponents.GeneratePairingCode goBack={childGoBack} goToStep={goToChildStep} />
        ),
      },
      {
        id: RegisterTerminalModalStripeSteps.PairYourTerminal,
        parentId: RegisterTerminalModalStripeSteps.GeneratePairingCode,
        title: STRIPE_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.PairYourTerminal].heading,
        stepperLabel: STRIPE_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.PairYourTerminal].title,
        component: (
          <StripeRegisterTerminalComponents.PairYourTerminal
            goBack={childGoBack}
            goToStep={goToChildStep}
            onSuccess={onComplete}
            onTerminalPaired={onRegisterTerminal}
            locationId={activeLocationId}
            stripeLocationId={stripeLocationId}
          />
        ),
      },
    ];
  }, [onRegisterTerminal, childGoBack, onGoBack, goToChildStep, S710TerminalDesignsWetag]);

  if (isLoading) {
    return <ContentLoader show={true} message={t('Loading terminal designs')} />;
  }
  return <RegisterTerminalStepper steps={steps} multiStepControl={multiStepControl} hideStepper={hideStepper} />;
};
